import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, QRCode, Space, theme, Typography } from 'antd';

import { Spin, NoData, CopyableText, ListBlock, Input } from 'components';

import { FormButtons } from 'modules';

import useForm from 'hooks/forms/useForm';
import useTheme from 'hooks/common/useTheme';
import useMyProfile from 'hooks/admin/useMyProfile';
import useValidation from 'hooks/forms/useValidation';
import { useTotpUrlQuery } from './hooks/useTotpUrlQuery';
import useNotifications from 'hooks/common/useNotifications';
import { useEnable2FaMutation } from './hooks/useEnable2FaMutation';

import { InputVariant } from 'components/inputs/Input';
import { ISecurityCheckFormProps } from './SecurityCheckForm.types';

import './SecurityCheckForm.styles.scss';

const { useToken } = theme;

const { Text } = Typography;

const SecurityCheckForm = ({ showGoBack, onGoBack, onFinish, onError }: ISecurityCheckFormProps) => {
  const { token } = useToken();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { notificationApi } = useNotifications();

  const rules = useValidation();
  const { isDarkMode } = useTheme();
  const { submitDisabled } = useForm(form);

  const { refetch } = useMyProfile();
  const { loading, data } = useTotpUrlQuery();
  const { loading: confirmLoading, refetch: enableUser2Fa } = useEnable2FaMutation();

  const { url, secret } = data || {};

  const qrCodeOptions = isDarkMode ? { color: token.colorWhite, bgColor: token.colorBgLayout } : {};

  const handleFinish = (codes: string[]) => {
    form.resetFields();
    onFinish(codes);
    refetch();
  };

  const handleError = ({ errorMessage }) => {
    onError();
    form.resetFields();
    notificationApi.error({ title: t(errorMessage) });
  };

  const handleSubmit = async ({ totp }) => {
    if (!totp) {
      return;
    }

    const { success, data, errorMessage } = await enableUser2Fa({ totp });

    if (success) {
      handleFinish(data.recovery_tokens);
    } else {
      handleError({ errorMessage });
    }
  };

  if (!url && !loading) {
    return <NoData />;
  }

  if (loading) {
    return <Spin />;
  }

  return (
    <div className="np-enable-ga-security-check">
      <Text className="np-enable-ga-security-check__text">1. {t('modal.twoFAEnable.formSteps.scanBarcode')}</Text>

      {url && <QRCode value={url} bordered={false} {...qrCodeOptions} />}

      <Text className="np-enable-ga-security-check__text">{t('modal.twoFAEnable.formSteps.enterKey')}</Text>

      {secret && (
        <Space className="np-enable-ga-security-check__code-wrapper">
          <ListBlock empty={!secret}>
            <CopyableText className={`np-enable-ga-security-check__key`}>{secret}</CopyableText>
          </ListBlock>
        </Space>
      )}

      <Text className="np-enable-ga-security-check__text">2. {t('modal.twoFAEnable.formSteps.enterCode')}</Text>

      <Form
        className={`np-enable-ga-security-check__form`}
        layout="vertical"
        form={form}
        name="register"
        onFinish={handleSubmit}
        scrollToFirstError
        requiredMark="optional"
      >
        <Form.Item name="totp" validateFirst rules={rules.ga}>
          <Input disabled={confirmLoading} autoFocus variant={InputVariant.BORDERED} />
        </Form.Item>

        <FormButtons
          showGoBack={showGoBack}
          submitDisabled={submitDisabled}
          loading={confirmLoading}
          marginTop={12}
          onGoBack={onGoBack}
          submitLabel="form.button.submit"
        />
      </Form>
    </div>
  );
};

export default SecurityCheckForm;
